import React from 'react'
import { Head } from '@inertiajs/react'
import {
  Users,
  Eye,
  TrendingDown,
  BarChart3,
  FileText,
  Share2,
  Globe,
  Monitor,
  Cpu,
  Smartphone,
} from 'lucide-react'
import { AdminLayout } from '@/components/admin/templates/AdminLayout'
import { Surface } from '@/components/admin/atoms/layout/Surface'
import { Stack } from '@/components/admin/atoms/layout/Stack'
import { Inline } from '@/components/admin/atoms/layout/Inline'
import { Grid } from '@/components/admin/atoms/layout/Grid'
import { Padding } from '@/components/admin/atoms/layout/Padding'
import { Heading } from '@/components/admin/atoms/typography/Heading'
import { PageHeader } from '@/components/admin/organisms/PageHeader'
import { AnalyticsChart } from '@/components/admin/atoms/analytics/AnalyticsChart'
import { DateRangePicker } from '@/components/admin/atoms/analytics/DateRangePicker'
import { TopStatCard } from '@/components/admin/atoms/analytics/TopStatCard'
import { AnalyticsTable } from '@/components/admin/atoms/analytics/AnalyticsTable'
import { VisitorsTable } from '@/components/admin/atoms/analytics/VisitorsTable'
import { Pagination } from '@/components/admin/molecules/data/Pagination'
import { useAnalyticsVM } from './Index.vm'
import type { PageProps } from '@/types/inertia.d'
import type { AnalyticsStats, AnalyticsPeriod, AnalyticsVisitorRow } from '@/types/analytics'
import type { PaginationMeta } from '@/types/shared'
import { countryWithFlag } from '@/utils/countryFlags'

type Props = PageProps<{ stats: AnalyticsStats; visitors: AnalyticsVisitorRow[]; visitorsPagination: PaginationMeta; period: AnalyticsPeriod }>

const AnalyticsIndex: React.FC<Props> = ({ stats, visitors, visitorsPagination, period }) => {
  const vm = useAnalyticsVM(period)

  return (
    <AdminLayout>
      <Head title="Analytics" />
      <Stack gap="6">
        <Inline justify="between" align="start">
          <PageHeader
            title="Analytics"
            description="Privacy-first website analytics. No cookies, no tracking."
          />
          <DateRangePicker
            activeRange={vm.activeRange}
            from={vm.customFrom}
            to={vm.customTo}
            onRangeChange={vm.onRangeChange}
            onCustomFromChange={vm.onCustomFromChange}
            onCustomToChange={vm.onCustomToChange}
          />
        </Inline>

        <Surface variant="card" radius="lg">
          <Inline justify="around" align="center">
            <TopStatCard
              label="Unique Visitors"
              value={stats.top_stats.unique_visitors}
              icon={<Users />}
              accentColor="primary"
            />
            <TopStatCard
              label="Total Pageviews"
              value={stats.top_stats.total_pageviews}
              icon={<Eye />}
              accentColor="info"
            />
            <TopStatCard
              label="Bounce Rate"
              value={`${stats.top_stats.bounce_rate}%`}
              icon={<TrendingDown />}
              accentColor="warning"
            />
            <TopStatCard
              label="Views / Visit"
              value={stats.top_stats.views_per_visit}
              icon={<BarChart3 />}
              accentColor="success"
            />
          </Inline>
        </Surface>

        <Surface variant="card" radius="lg">
          <Padding all="5">
            <Stack gap="3">
              <Heading level={5}>Visitors &amp; Pageviews</Heading>
              <AnalyticsChart data={stats.timeseries} />
            </Stack>
          </Padding>
        </Surface>

        <Grid cols="2" gap="4">
          <Surface variant="card" radius="lg">
            <Padding all="5">
              <Stack gap="3">
                <Heading level={5}>Top Pages</Heading>
                <AnalyticsTable
                  title="Top Pages"
                  labelHeader="Page"
                  icon={<FileText />}
                  rows={stats.top_pages.map((p) => ({
                    label: p.path,
                    visitors: p.visitors,
                    views: p.views,
                  }))}
                />
              </Stack>
            </Padding>
          </Surface>

          <Surface variant="card" radius="lg">
            <Padding all="5">
              <Stack gap="3">
                <Heading level={5}>Top Sources</Heading>
                <AnalyticsTable
                  title="Top Sources"
                  labelHeader="Source"
                  icon={<Share2 />}
                  rows={stats.top_sources.map((s) => ({
                    label: s.source,
                    visitors: s.visitors,
                    views: s.views,
                  }))}
                />
              </Stack>
            </Padding>
          </Surface>
        </Grid>

        <Grid cols="3" gap="4">
          <Surface variant="card" radius="lg">
            <Padding all="5">
              <Stack gap="3">
                <Heading level={5}>Browsers</Heading>
                <AnalyticsTable
                  title="Browsers"
                  labelHeader="Browser"
                  icon={<Globe />}
                  rows={stats.browsers.map((b) => ({
                    label: b.name,
                    visitors: b.visitors,
                    views: b.views,
                  }))}
                />
              </Stack>
            </Padding>
          </Surface>

          <Surface variant="card" radius="lg">
            <Padding all="5">
              <Stack gap="3">
                <Heading level={5}>Operating Systems</Heading>
                <AnalyticsTable
                  title="OS"
                  labelHeader="OS"
                  icon={<Monitor />}
                  rows={stats.os.map((o) => ({
                    label: o.name,
                    visitors: o.visitors,
                    views: o.views,
                  }))}
                />
              </Stack>
            </Padding>
          </Surface>

          <Surface variant="card" radius="lg">
            <Padding all="5">
              <Stack gap="3">
                <Heading level={5}>Devices</Heading>
                <AnalyticsTable
                  title="Devices"
                  labelHeader="Device"
                  icon={<Smartphone />}
                  rows={stats.devices.map((d) => ({
                    label: d.name,
                    visitors: d.visitors,
                    views: d.views,
                  }))}
                />
              </Stack>
            </Padding>
          </Surface>
        </Grid>

        {stats.countries.length > 0 && (
          <Surface variant="card" radius="lg">
            <Padding all="5">
              <Stack gap="3">
                <Heading level={5}>Countries</Heading>
                <AnalyticsTable
                  title="Countries"
                  labelHeader="Country"
                  icon={<Globe />}
                  rows={stats.countries.map((c) => ({
                    label: countryWithFlag(c.country),
                    visitors: c.visitors,
                    views: c.views,
                  }))}
                />
              </Stack>
            </Padding>
          </Surface>
        )}

        <Surface variant="card" radius="lg">
          <Padding all="5">
            <Stack gap="3">
              <Heading level={5}>Recent Visitors</Heading>
              <VisitorsTable visitors={visitors} />
              <Pagination meta={visitorsPagination} onPageChange={vm.handleVisitorsPageChange} />
            </Stack>
          </Padding>
        </Surface>
      </Stack>
    </AdminLayout>
  )
}

export default AnalyticsIndex
